import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toastDefaults } from '../../../utils/ToastOptionCommon';
import { toast } from 'react-toastify';
import axiosCall from '../../../utils/axiosCall';
import { resetProperty } from '../../../redux/slices/CreateLisingSlice';
import { useLocation, useParams } from 'react-router';
import Loader from '../../../components/CommonComponents/Loader/Loader';
import LocationComponent from '../../../components/AddListingsComponents/LocationComponent/LocationComponent';
import DetailedInfoComponent, {
  Amenity,
} from '../../../components/AddListingsComponents/DetailedInfoComponent/DetailedInfoComponent';
import NearByComponent from '../../../components/AddListingsComponents/NearByComponent/NearByComponent';
import AddMediaComponent from '../../../components/AddListingsComponents/AddMediaComponent/AddMediaComponent';
import HeadingStatusComponent from '../../../components/AddListingsComponents/HeadingStatusComponent/HeadingStatusComponent';
import CreateListingComponent from '../../../components/AddListingsComponents/CreateListingComponent/CreateListingComponent';

import {
  approveProperty,
  approveSellProperty,
  fetchPropertyById,
  fetchSellPropertyById,
  propertyById,
  resetpropertyData,
} from '../../../redux/slices/PropertySlice';
import { UnknownAction } from '@reduxjs/toolkit';
import { nearby } from '../../../utils/addListingInitialValue';
import { ListingSection, SectionData } from '../../../constants/ListingSection';
import NavigationPrompt from '../../../utils/NavigationPrompt/NavigationPrompt';
import { IoWarningOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router';
import DashboardHeader from '../../../components/DashboardComponents/DashboardHeader/DashboardHeader';
import { Button } from 'react-bootstrap';
import Busy from '../../../assets/gif/busy.gif';
import { dealPropertyType, dealTypeEnum } from '../../../constants/Property';

function EditListing() {
  const { id }: any = useParams();
  const [propertyId, setPropertyId] = useState(id);
  const [currentSection, setCurrentSection] = useState<number>(-1);
  const propertyState = useSelector((state: any) => state.property);
  const property = useSelector(propertyById);
  const dispatch = useDispatch();
  const isDocUpdate = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dealType = location?.state?.dealType;
  const propertyBuildingDetails =
    property?.buildingDetails || property?.propertyDetails?.buildingDetails || {};

  const {
    approvePropertySuccess,
    approvePropertyLoading,
    approvePropertyError,
  } = useSelector((state: any) => state.property);
  useEffect(() => {
    if (id) {
      setPropertyId(id);
      dispatch(
        dealType == dealTypeEnum.Sell
          ? (fetchSellPropertyById({
              Id: id,
              section: currentSection,
            }) as unknown as UnknownAction)
          : (fetchPropertyById({
              Id: id,
              section: currentSection,
            }) as unknown as UnknownAction)
      );
    }
    return () => {
      dispatch(resetpropertyData());
      dispatch(resetProperty());
    };
  }, [id]);

  const industry = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) => item.name === 'Industry'
  );
  const education = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) => item.name === 'Education'
  );
  const grocery = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) => item.name === 'Grocery'
  );
  const healthAndMedical = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) => item.name === 'Healthcare'
  );
  const militaryBase = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) =>
      item.name === 'MilitaryBase'
  );
  const movieTheater = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) =>
      item.name === 'Movie Theater'
  );
  const park = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) => item.name === 'Park'
  );
  const restaurant = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) => item.name === 'Restaurant'
  );
  const shoppingCenter = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) => item.name === 'Shopping'
  );
  const transportation = property.nearby_facilities?.filter(
    (item: { name: string; description: string }) => item.name === 'Transport'
  );

  const editListingInitialValues = {
    section: -1,
    details: {
      owner: property?.OwnerName,
      dealType: property?.dealType || 'rent',
      propertyTitle: property.title || '',
      propertyDescription: property.description || '',
      propertyType: property.p_type || '',
      propertyStatus: property.status,
      propertyRooms:
        property.dealType === dealTypeEnum.Sell
          ? property.propertyDetails.bedrooms
          : property.numberOfRooms || 1,
      propertyRoomType:
        // For sell properties, create a single room type with the property details
        property.dealType === dealTypeEnum.Sell
          ? [{ price: property.price, availableFrom: property.available_date }]
          : // For rent properties, use the existing rooms array
            property.rooms || [],
      superBuiltUpArea: property.propertyDetails?.superBuiltUpArea || '',
      superBuiltUpAreaUnit:
        property.propertyDetails?.superBuiltUpAreaUnit || '',
      carpetArea: property.propertyDetails?.carpetArea || '',
      carpetAreaUnit: property.propertyDetails?.carpetAreaUnit || '',
      constructionYear: property.propertyDetails?.constructionYear || '',
      furnishingStatus: property.propertyDetails?.furnishingStatus || '',
      facing: property.propertyDetails?.facing || '',
      propertyAge: property.propertyDetails?.propertyAge || '',
      possessionStatus: property.propertyDetails?.possessionStatus || '',
      totalFloors: property.propertyDetails?.totalFloors || 0,
      floorNumber: property.propertyDetails?.floorNumber || 0,
      buildingDetails: {
        yearBuilt: propertyBuildingDetails.yearBuilt || '',
        lastRenovatedYear:
          propertyBuildingDetails.lastRenovatedYear || '',
        renovationDetails: propertyBuildingDetails.renovationDetails || '',
        condition: propertyBuildingDetails.condition || '',
      },
      propertyPrice: 0,
      propertyTenure: 'm',
      isEntireUnit: property.isEntireUnit || false,
      totalBathroom:
        property.dealType === dealTypeEnum.Sell
          ? property.propertyDetails.bathrooms
          : property.totalBathroom || 0,
      totalBed: property.totalBed || 0,
      withFood: property.withFood || '',
      isFeaturedProperty: property.isFeaturedProperty || false,
    },
    location: {
      addressLine1: property.address?.addressLine1 || '',
      street: property.address?.street || '',
      state: {
        label: property.address?.state || '',
        value: property.address?.code || '',
      },
      city: {
        label: property.address?.city || '',
        value: property.address?.code || '',
      },
      zip: property.address?.zip || '',
      latitude: property.address?.latitude || 31.9686,
      longitude: property.address?.longitude || -99.9018,
    },
    amenities: {
      p_video_path: property.p_video_path || '',
      amenities:
        property?.amenities?.map((e: Amenity) => e?.id?.toString()) || [],
      other: property?.other || '',
    },
    nearBy: {
      propertyCafe: industry?.length > 0,
      propertyCafeDetails: industry?.length > 0 ? industry : nearby,
      propertyEducation: education?.length > 0,
      propertyEducationDetails: education?.length > 0 ? education : nearby,
      propertyGroceryStore: grocery?.length > 0,
      propertyGroceryStoreDetails: grocery?.length > 0 ? grocery : nearby,
      propertyHealthAndMedical: healthAndMedical?.length > 0,
      propertyHealthAndMedicalDetails:
        healthAndMedical?.length > 0 ? healthAndMedical : nearby,
      propertyMilitaryBase: militaryBase?.length > 0,
      propertyMilitaryBaseDetails:
        militaryBase?.length > 0 ? militaryBase : nearby,
      propertyMovieTheaters: movieTheater?.length > 0,
      propertyMovieTheatersDetails:
        movieTheater?.length > 0 ? movieTheater : nearby,
      propertyParks: park?.length > 0,
      propertyParksDetails: park?.length > 0 ? park : nearby,
      propertyRestaurants: restaurant?.length > 0,
      propertyRestaurantsDetails: restaurant?.length > 0 ? restaurant : nearby,
      propertyShoppingCenters: shoppingCenter?.length > 0,
      propertyShoppingCentersDetails:
        shoppingCenter?.length > 0 ? shoppingCenter : nearby,
      propertyTransportation: transportation?.length > 0,
      propertyTransportationDetails:
        transportation?.length > 0 ? transportation : nearby,
    },
    propertyMedia: [],
    propertyBathrooms: 1,
    noOfGarages: property.noOfGarages || 0,
  };

  const handleSubmit = async (formData: FormData) => {
    try {
      setIsLoading(true);
      formData.append(`propertyId`, propertyId);
      const dealType = propertyState?.property?.dealType;
      const baseUrl =
        dealType === dealTypeEnum.Sell ? '/sell-property' : '/properties';
      const url = `${baseUrl}/update/${propertyId}`;
      // edit request
      const response = await axiosCall.put(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const data = response.data;
      if (response.status === 201 || response.status === 200) {
        toast.success(
          `${SectionData[currentSection]} updated successfully. Please publish your property.`,
          toastDefaults()
        );
        setCurrentSection(-99);
        // props?.resetForm();
        // dispatch(resetProperty());
        // dispatch(resetpropertyData());
        // navigate('/dashboard/listing');
        dispatch(
          dealType === dealTypeEnum.Sell
            ? (fetchSellPropertyById({
                Id: id,
                section: currentSection,
              }) as unknown as UnknownAction)
            : (fetchPropertyById({
                Id: id,
                section: currentSection,
              }) as unknown as UnknownAction)
        );
      } else if (response?.status === 403) {
        toast.info(
          'You cannot make feature more properties under your current plan.',
          toastDefaults()
        );
      } else {
        toast.error('Failed to update property listing.', toastDefaults());
      }
    } catch (error: any) {
      console.error(`Edit Listing error: ${error}`);
      if (error?.response?.status === 409) {
        toast.error('Property title already exist.', toastDefaults());
      } else if (error?.response?.status === 403) {
        toast.info(
          'You cannot make feature more properties under your current plan.',
          toastDefaults()
        );
      } else {
        toast.error('Failed to update property listing.', toastDefaults());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAprroveProperty = async () => {
    if (
      editListingInitialValues.location.state.value === '' ||
      editListingInitialValues.location.city.value === ''
    ) {
      toast.info('Please add location.', toastDefaults());
      return;
    } else if (editListingInitialValues.amenities.amenities.length === 0) {
      toast.info('Please add amenities.', toastDefaults());
      return;
    }

    let body = {
      isApproved: true,
      propertyId: propertyId,
    };

    const resultAction = await dispatch(
      dealType === dealTypeEnum.Sell
        ? (approveSellProperty({ body }) as unknown as UnknownAction)
        : (approveProperty({ body }) as unknown as UnknownAction)
    );

    // Choose the correct action reference
    const actionType =
      dealType === dealTypeEnum.Sell ? approveSellProperty : approveProperty;

    if (actionType.fulfilled.match(resultAction)) {
      toast.success('Property is published successfully 🎉', {
        position: 'top-right',
        autoClose: 3000,
      });
      navigate('/dashboard/listing');
    } else if (actionType.rejected.match(resultAction)) {
      const err = resultAction.payload as any;
      if (err?.status === 403) {
        toast.info('Please subscribe a package!', toastDefaults());
        navigate('/dashboard/Package');
      } else {
        toast.error(err?.data?.error || 'Failed to publish property 🚨', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    }
  };

  return (
    <div id="createListingTab" className="main-content">
      {(isLoading || propertyState.isLoading) && <Loader />}

      <NavigationPrompt
        confirmationText={
          'Are you sure you want to leave without saving changes?'
        }
        icon={<IoWarningOutline className="m-1 warn-popup" />}
        saveButtonText={'Leave'}
        cancelButtonText={'Stay'}
        modalTitle={'Confirmation'}
        isProgress={false}
      />
      <DashboardHeader hideListingBtn={true} />
      {/* Add property heading */}
      <HeadingStatusComponent isEditListing={true} section={currentSection} />
      <div className="font-body">
        <>{console.log(editListingInitialValues)}</>
        {/* Create Listing */}
        <CreateListingComponent
          handleSubmit={handleSubmit}
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          initialValues={editListingInitialValues}
          isEdit={true}
          // dealType={editListingInitialValues.details.dealType}
        />
        {/* Location */}
        <LocationComponent
          dealType={editListingInitialValues.details.dealType}
          handleSubmit={handleSubmit}
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          initialValues={editListingInitialValues}
          isEdit={true}
          savedSection={{}}
        />
        {/* Detailed Information */}
        <DetailedInfoComponent
          handleSubmit={handleSubmit}
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          initialValues={editListingInitialValues}
          isEdit={true}
        />
        {/* Near by */}
        <NearByComponent
          handleSubmit={handleSubmit}
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          initialValues={editListingInitialValues}
          isEdit={true}
        />
        {/* Media & Submit */}
        <AddMediaComponent
          lastUploadedFilePath={property?.documentsLink}
          isEdit={true}
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          handleSubmit={handleSubmit}
          isDocUpdate={isDocUpdate}
        />

        <div className="publish box-footer flex justify-end gap-3 text-center">
          <Button
            type="submit"
            className="btn btn-primary"
            disabled={
              property?.is_active || propertyState?.approvePropertyLoading
            }
            onClick={() => handleAprroveProperty()}
            // disabled={props?.isSubmitting}
          >
            {propertyState?.approvePropertyLoading && (
              <img
                src={Busy}
                alt="loading.."
                className="me-2 mb-1"
                style={{ maxWidth: '16px' }}
              />
            )}
            Publish
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditListing;
